/**
 * Spanish (Mexico), reference catalog for the admin SPA.
 *
 * @equinox/shared ships its own es-MX catalog for the POS terminal. This one
 * is scoped to the admin backend and lives in packages/admin because the
 * shared package is owned by another agent working in parallel on this
 * branch set (see docs/decisions/2026-07-31-admin-i18n-catalog.md). Same
 * rule applies here as everywhere else: no user-visible string is written
 * inline, every one of them is a key in this file.
 *
 * Tone: plain, direct, formal but not stiff. Correct accents and
 * capitalization. Sentence case. No exclamation marks.
 */

export const adminEsMX = {
  'app.title': 'Equinox — Administración',

  // Generic
  'common.save': 'Guardar',
  'common.cancel': 'Cancelar',
  'common.create': 'Crear',
  'common.delete': 'Eliminar',
  'common.edit': 'Editar',
  'common.close': 'Cerrar',
  'common.confirm': 'Confirmar',
  'common.loading': 'Cargando…',
  'common.error': 'Ocurrió un error',
  'common.retry': 'Reintentar',
  'common.required': 'Campo obligatorio',
  'common.search': 'Buscar',
  'common.active': 'Activo',
  'common.inactive': 'Inactivo',
  'common.actions': 'Acciones',
  'common.yes': 'Sí',
  'common.no': 'No',
  'common.back': 'Volver',
  'common.signOut': 'Cerrar sesión',
  'common.status': 'Estado',
  'common.all': 'Todos',
  'common.none': 'Ninguno',
  'common.copy': 'Copiar',
  'common.copied': 'Copiado',
  'common.saved': 'Cambios guardados',

  // Navigation
  'nav.products': 'Productos',
  'nav.categories': 'Categorías',
  'nav.discountReasons': 'Motivos de descuento',
  'nav.staff': 'Personal',
  'nav.terminals': 'Terminales',
  'nav.paymentTerminals': 'Terminales de cobro',
  'nav.reports': 'Reportes',
  'nav.shifts': 'Cortes de turno',
  'nav.sync': 'Estado de sincronización',
  'nav.ticket': 'Ticket',
  'terminals.timeZone': 'Zona horaria',
  'terminals.timeZoneTitle': 'Zona horaria de la terminal',
  'terminals.timeZoneLabel': 'Zona horaria (nombre IANA)',
  'terminals.timeZoneHint': 'Por ejemplo America/Mexico_City. Escriba para buscar en la lista.',
  'terminals.timeZoneReported': 'El dispositivo reporta la zona {zone}. Ajustar una manual solo es necesario si esa es incorrecta.',
  'terminals.timeZoneNotReported': 'El dispositivo aún no reporta su zona horaria; se usa la zona del negocio.',
  'terminals.timeZoneUseDevice': 'Usar la del dispositivo',
  'terminals.timeZoneManual': 'manual',
  'login.downloadApk': 'Descargar la aplicación para terminal (APK)',

  // Login
  'login.title': 'Ingreso de administrador',
  'login.email': 'Correo electrónico',
  'login.password': 'Contraseña',
  'login.submit': 'Entrar',
  'login.submitting': 'Entrando…',
  'login.error': 'Correo o contraseña incorrectos',
  'login.networkError': 'No se pudo conectar con el servidor. Intente de nuevo',
  // Shown while a session check could not get an answer. It says the session
  // survives on purpose: the whole point of this state is that we did NOT sign
  // anyone out over a blip. See state/authStore.ts.
  'login.sessionUnreachable': 'No se pudo confirmar la sesión. Se reintentará solo; la sesión sigue abierta',

  // Products
  'products.title': 'Productos',
  'products.subtitle': 'Catálogo, precios e imágenes',
  'products.search': 'Buscar por nombre o SKU',
  'products.category': 'Categoría',
  'products.allCategories': 'Todas las categorías',
  'products.new': 'Nuevo producto',
  'products.name': 'Nombre',
  'products.sku': 'SKU',
  'products.price': 'Precio',
  'products.cost': 'Costo',
  'products.options.title': 'Opciones (sabores)',
  'products.options.enable': 'Este producto se vende por opciones',
  'products.options.mixHint': 'Cada opción tiene su precio, costo y existencia. Si se eligen varias, se cobra la más cara.',
  'products.options.label': 'Pregunta al elegir',
  'products.options.labelPlaceholder': 'Sabor',
  'products.options.max': 'Máximo a elegir',
  'products.options.maxPlaceholder': 'Sin límite',
  'products.options.name': 'Opción',
  'products.options.add': 'Agregar opción',
  'products.options.remove': 'Quitar',
  'products.options.moveUp': 'Subir',
  'products.options.moveDown': 'Bajar',
  'products.options.fromPrice': 'Precio (desde)',
  'products.options.priceDerived': 'El precio del producto es el de la opción activa más barata.',
  'products.options.maxInvalid': "Capture un número entero de 1 en adelante, o déjelo vacío",
  'products.options.savedButStock': "El producto ya se guardó, pero no se pudo ajustar la existencia de una opción: vuelva a guardar.",
  'products.options.problem.noneActive': "Deje al menos una opción activa.",
  'products.options.stockOnOptions': "La existencia se cuenta por opción: edite el producto",
  'products.options.addon': "Complemento (se suma al precio; no es un sabor)",
  'products.options.addonPrice': "Precio extra",
  'products.options.addonNote': "Un complemento no lleva costo ni existencia: se cobra sobre el sabor.",
  'products.options.group': "Columna",
  'products.options.groupPlaceholder': "Dulces, Quesos, Salados…",
  'products.options.mixLegend': "Precio por mezcla",
  'products.options.mixMin': "Desde cuántos sabores",
  'products.options.mixNone': "Sin precio de mezcla",
  'products.options.mixPrice': "Precio desde ese número",
  'products.options.mixInvalid': "Capture ambos: un número entero de 2 en adelante y el precio, o deje los dos vacíos",
  'products.options.addonsMax': "Máximo de complementos",
  'products.needsKitchen': "Va a cocina (aparece en la comanda)",
  'products.needsKitchenHint': "Desmárquelo para frascos, botanas y lo que no se prepara: no genera comanda.",
  'products.options.problem.none': 'Agregue al menos una opción, o desactive las opciones.',
  'products.options.problem.nameRequired': 'Cada opción necesita un nombre.',
  'products.options.problem.priceRequired': 'Cada opción necesita un precio.',
  'products.options.problem.duplicateName': 'Dos opciones no pueden llamarse igual.',
  'products.options.problem.invalidNumber': 'Revise los montos y existencias de las opciones.',
  'products.fromPriceCell': '{price}+',
  'products.fromPriceLead': 'Desde',
  'products.costHint': 'Costo unitario, opcional. Solo visible para administradores; nunca llega a las terminales.',
  'products.priceCentsPreview': 'Se guardará como {cents} centavos',
  'products.priceInvalid': 'Capture un monto válido, hasta dos decimales',
  'products.image': 'Imagen',
  'products.imageUrl': 'URL de la imagen',
  'products.imageHelp': 'Opcional. Si no hay imagen, se usa la inicial del producto',
  'products.sortOrder': 'Orden',
  'products.save': 'Guardar producto',
  'products.saved': 'Producto guardado',
  'products.deactivate': 'Desactivar',
  'products.deactivateConfirm': 'El producto dejará de aparecer en el punto de venta. Puede reactivarlo después',
  'products.activate': 'Reactivar',
  'products.noResults': 'Ningún producto coincide con la búsqueda',
  'products.editPrice': 'Editar precio',
  'products.cancelEdit': 'Cancelar edición',

  // Product import
  'products.import.action': 'Importar CSV',
  'products.import.title': 'Importar productos desde CSV',
  'products.import.help': 'El archivo debe incluir columnas: sku, name, category, price, image_url (opcional)',
  'products.import.selectFile': 'Seleccionar archivo',
  'products.import.dryRunButton': 'Previsualizar cambios',
  'products.import.previewTitle': 'Previsualización — nada se ha guardado todavía',
  'products.import.row': 'Fila',
  'products.import.willCreate': 'Se creará',
  'products.import.willUpdate': 'Se actualizará',
  'products.import.willSkip': 'Se omitirá',
  'products.import.errors': 'Errores',
  'products.import.noErrors': 'Sin errores',
  'products.import.confirmButton': 'Confirmar e importar',
  'products.import.confirming': 'Importando…',
  'products.import.success': 'Importación completada: {count} filas aplicadas',
  'products.import.hasErrorsWarning': 'Hay filas con errores. Esas filas no se aplicarán',
  'products.import.selectFileFirst': 'Seleccione un archivo CSV primero',

  // Categories
  'categories.title': 'Categorías',
  'categories.subtitle': 'Color y orden con el que aparecen en el punto de venta',
  'categories.new': 'Nueva categoría',
  'categories.name': 'Nombre',
  'categories.color': 'Color',
  'categories.sortOrder': 'Orden',
  'categories.preview': 'Así se verá en el punto de venta',
  'categories.save': 'Guardar categoría',
  'categories.deactivate': 'Desactivar',
  'categories.deactivateConfirm': 'La categoría dejará de aparecer en el punto de venta',
  'categories.activate': 'Reactivar',
  'categories.noProducts': 'Sin productos',

  // Discount reasons
  'discountReasons.title': 'Motivos de descuento',
  'discountReasons.subtitle': 'Un descuento nunca se aplica sin un motivo. Estos son los disponibles',
  'discountReasons.new': 'Nuevo motivo',
  'discountReasons.label': 'Motivo',
  'discountReasons.requiresNote': 'Requiere nota adicional',
  'discountReasons.save': 'Guardar motivo',
  'discountReasons.deactivate': 'Desactivar',
  'discountReasons.deactivateConfirm': 'El motivo dejará de estar disponible en el punto de venta',

  // Staff
  'staff.title': 'Personal',
  'staff.subtitle': 'Claves, límites de descuento y acceso',
  'staff.new': 'Nuevo integrante',
  'staff.code': 'Clave de empleado',
  'staff.displayName': 'Nombre',
  'staff.email': 'Correo (opcional, solo para administradores)',
  'staff.role': 'Rol',
  'staff.roleStaff': 'Personal',
  'staff.roleAdmin': 'Administrador',
  'staff.discountCeiling': 'Límite de descuento (%)',
  'staff.discountCeilingHelp': 'De 0 a 100 %. Descuentos mayores requieren autorización de un administrador',
  'staff.pinSection': 'NIP de acceso a la terminal',
  'staff.pin': 'Nuevo NIP (4 a 6 dígitos)',
  'staff.pinConfirm': 'Confirmar NIP',
  'staff.pinMismatch': 'Los NIP no coinciden',
  'staff.pinInvalid': 'El NIP debe tener entre 4 y 6 dígitos',
  'staff.pinHashedNotice': 'El NIP se guarda cifrado. Nunca se muestra ni se puede recuperar, solo reemplazar',
  'staff.pinNeverShown': 'NIP no disponible para mostrar',
  'staff.setPin': 'Establecer NIP',
  'staff.pinSet': 'NIP actualizado',
  'staff.password': 'Contraseña de administrador (mínimo 12 caracteres)',
  'staff.passwordHelp': 'Solo aplica al rol administrador. Se guarda cifrada',
  'staff.save': 'Guardar integrante',
  'staff.deactivate': 'Desactivar',
  'staff.deactivateConfirm': 'La persona no podrá iniciar sesión en ninguna terminal',
  'staff.activate': 'Reactivar',
  'staff.failedAttempts': 'Intentos fallidos',
  'staff.lockedUntil': 'Bloqueado hasta {time}',

  // Terminals
  'terminals.title': 'Terminales',
  'terminals.subtitle': 'Dispositivos autorizados a vender',
  'terminals.new': 'Registrar terminal',
  'terminals.name': 'Nombre de la terminal',
  'terminals.register': 'Registrar',
  'terminals.appVersion': 'Versión de la app',
  'terminals.device': 'Dispositivo',
  'terminals.deviceUnknown': 'Sin información',
  'terminals.lastSeen': 'Última conexión',
  'terminals.lastSync': 'Última sincronización',
  'terminals.never': 'Nunca',
  'terminals.issueToken': 'Emitir token',
  'terminals.issueTokenConfirm': 'Se generará un nuevo token para esta terminal. Cualquier token anterior dejará de funcionar',
  'terminals.tokenOnceTitle': 'Token de la terminal',
  'terminals.tokenOnceWarning': 'Este token no se volverá a mostrar. Cópielo ahora y configúrelo en la terminal',
  'terminals.revoke': 'Revocar token',
  'terminals.revokeConfirm': 'La terminal dejará de poder sincronizar hasta que se le emita un nuevo token',
  'terminals.deactivate': 'Desactivar terminal',
  'terminals.deactivateConfirm': 'La terminal dejará de estar autorizada para vender',
  'terminals.activate': 'Reactivar',
  'terminals.quiet': 'Sin actividad reciente',
  'terminals.quietWarning': 'Esta terminal no sincroniza desde hace más de {hours} horas',

  // Payment terminals (Mercado Pago / Clip push) — deliberately a separate
  // namespace from "terminals" above, which is the POS device.
  'paymentTerminals.title': 'Terminales de cobro',
  'paymentTerminals.subtitle': 'Terminales Mercado Pago y Clip para cobrar sin teclear el número de autorización',
  'paymentTerminals.new': 'Registrar terminal de cobro',
  'paymentTerminals.processor': 'Proveedor',
  'paymentTerminals.processorMercadoPago': 'Mercado Pago',
  'paymentTerminals.processorClip': 'Clip',
  'paymentTerminals.externalTerminalId': 'Identificador de la terminal',
  'paymentTerminals.externalTerminalIdHintMercadoPago': 'El terminal_id de Mercado Pago (modo PDV)',
  'paymentTerminals.externalTerminalIdHintClip': 'El número de serie del Clip Total / Pin Pad',
  'paymentTerminals.displayName': 'Nombre para mostrar',
  'paymentTerminals.displayNameHint': 'Cómo se verá en la caja, por ejemplo «Caja 1 — Clip Total»',
  'paymentTerminals.save': 'Guardar terminal de cobro',
  'paymentTerminals.deactivate': 'Desactivar',
  'paymentTerminals.deactivateConfirm': 'La terminal dejará de aparecer como opción de cobro en la caja',
  'paymentTerminals.activateConfirm': 'La terminal volverá a aparecer como opción de cobro en la caja',
  'paymentTerminals.activate': 'Reactivar',
  'paymentTerminals.empty': 'Todavía no hay terminales de cobro registradas',

  // Reports
  'reports.title': 'Reportes',
  'reports.subtitle': 'Ventas, descuentos y desempeño por periodo',
  'reports.period.today': 'Hoy',
  'reports.period.week': 'Esta semana',
  'reports.period.month': 'Este mes',
  'reports.period.custom': 'Periodo personalizado',
  'reports.from': 'Desde',
  'reports.to': 'Hasta',
  'reports.filterMethod': 'Forma de pago',
  'reports.filterStaff': 'Personal',
  'reports.filterTerminal': 'Terminal',
  'reports.apply': 'Aplicar filtros',
  'reports.grossSales': 'Venta bruta',
  'reports.netSales': 'Venta neta',
  'reports.transactions': 'Transacciones',
  'reports.itemsSold': 'Artículos vendidos',
  'reports.discountTotal': 'Total de descuentos',
  'reports.averageTicket': 'Ticket promedio',
  'reports.costOfGoods': 'Costo de lo vendido',
  'reports.netProfit': 'Utilidad neta',
  'reports.itemsWithoutCost':
    '{count} artículos vendidos no tienen costo registrado; la utilidad no los descuenta. Capture el costo en Productos para un cálculo completo.',
  'reports.byMethod': 'Por forma de pago',
  'reports.byReason': 'Descuentos por motivo',
  'reports.topByUnits': 'Más vendidos por unidades',
  'reports.topByRevenue': 'Más vendidos por ingreso',
  'reports.noData': 'No hay datos en este periodo',
  'reports.methodCash': 'Efectivo',
  'reports.methodCard': 'Tarjeta',

  // Shift reconciliation
  'shifts.title': 'Cortes de turno',
  'shifts.subtitle': 'Efectivo declarado contra efectivo esperado, por turno. Esta información es exclusiva del administrador',
  'shifts.staff': 'Personal',
  'shifts.terminal': 'Terminal',
  'shifts.opened': 'Apertura',
  'shifts.closed': 'Cierre',
  'shifts.status': 'Estado',
  'shifts.statusOpen': 'Abierto',
  'shifts.statusClosed': 'Cerrado',
  'shifts.openingBalance': 'Fondo inicial',
  'shifts.closingBalance': 'Efectivo declarado',
  'shifts.expectedCash': 'Efectivo esperado',
  'shifts.variance': 'Diferencia',
  'shifts.varianceOver': 'Sobrante de {amount}',
  'shifts.varianceShort': 'Faltante de {amount}',
  'shifts.varianceNone': 'Sin diferencia',
  'shifts.noData': 'No hay turnos en este periodo',
  'shifts.confidential': 'Esta pantalla es de uso exclusivo del administrador. El personal nunca ve esta información',

  // Sync health
  'sync.title': 'Estado de sincronización',
  'sync.subtitle': 'Pendientes y rechazos por terminal',
  'sync.terminal': 'Terminal',
  'sync.pendingCount': 'Envíos pendientes',
  'sync.rejectedTitle': 'Registros rechazados',
  'sync.rejectedEmpty': 'No hay registros rechazados',
  'sync.rejectedCode': 'Código',
  'sync.rejectedMessage': 'Motivo',
  'sync.rejectedAt': 'Recibido',
  'sync.rejectedKind': 'Tipo',
  'sync.needsAttention': 'Requiere atención',
  'sync.allClear': 'Todo sincronizado',
  'sync.rejectedAction': 'Acción',
  'sync.resolve': 'Marcar como resuelto',

  // Impersonation
  'impersonation.start': 'Ver como esta persona',
  'impersonation.confirmTitle': 'Ver el sistema como {name}',
  'impersonation.confirmBody': 'Verá la interfaz exactamente como la ve esta persona, en modo de solo lectura. Ninguna acción que realice mientras tanto se guardará',
  'impersonation.confirmButton': 'Entrar en modo de vista',
  'impersonation.bannerLabel': 'Viendo como: {name} — modo de solo lectura',
  'impersonation.exit': 'Salir del modo de vista',
  'impersonation.readOnlyNotice': 'Esta acción está deshabilitada durante el modo de vista',

  // Errors / http
  'error.network': 'No se pudo conectar con el servidor',
  'error.unauthorized': 'Su sesión expiró. Vuelva a iniciar sesión',
  'error.forbidden': 'No tiene permiso para esta acción',
  'error.validation': 'Revise los datos capturados',
  'error.notFound': 'No se encontró el recurso',
  'error.unknown': 'Ocurrió un error inesperado',

  // Route error boundary
  'error.boundaryTitle': 'Algo salió mal',
  'error.boundaryMessage':
    'Ocurrió un error inesperado en esta pantalla. Nada de lo que había capturado se perdió. Recargue la página e intente de nuevo',
  'error.reload': 'Recargar página',

  // Field-level validation (server-reported issues surfaced next to the field)
  'validation.minLength': 'Mínimo {min} caracteres',
  'validation.invalidEmail': 'Correo electrónico no válido',
  'validation.fieldInvalid': 'Valor no válido',
  'validation.outOfRange': 'Debe estar entre {min} y {max}',

  // Staff — validation specifics (mirrors packages/server/src/routes/staff.ts)
  'staff.passwordMinLength': 'Mínimo 12 caracteres',
  'staff.passwordTooShort': 'La contraseña debe tener al menos 12 caracteres',
  'staff.passwordRequiredForAdmin': 'La contraseña es obligatoria para el rol administrador',
  'staff.discountCeilingInvalid': 'Capture un número entre 0 y 100, hasta dos decimales',
  'staff.codeInUse': 'Esa clave de empleado ya está en uso',
  // stock & media
  'products.editTitle': 'Editar producto',
  'products.duplicate': 'Duplicar',
  'products.duplicateTitle': 'Duplicar producto',
  'products.duplicateSkuHint': 'Capture un SKU distinto al del producto original',
  'products.stock': 'Existencia',
  'products.stockAdjustLabel': 'Ajustar existencia a',
  'products.stockOpeningLabel': 'Existencia inicial',
  'products.stockInvalid': 'Capture un número, hasta tres decimales',
  'products.batch.toolbarLabel': 'Acciones para la selección',
  'products.batch.selectAll': 'Seleccionar todos los productos filtrados',
  'products.batch.selectRow': 'Seleccionar {name}',
  'products.batch.selectedCount': '{count} seleccionados',
  'products.batch.activate': 'Reactivar selección',
  'products.batch.deactivate': 'Desactivar selección',
  'products.batch.clearSelection': 'Quitar selección',
  'products.batch.someFailed': '{count} no se pudieron actualizar',
  'categories.imageUrl': 'Imagen',
  'media.upload': 'Subir imagen',
  'media.uploading': 'Subiendo…',
  'media.orUrl': 'O bien, pegue una URL de imagen',
  'media.clickToUpload': 'Haga clic o presione Entrar para cambiar la imagen',

  // Table edit mode (2026-07-31) — batch edition of products right from the
  // catalog table, see docs/decisions/2026-07-31-products-table-edit-mode.md.
  'products.tableEdit.toggle': 'Edición en tabla',
  'products.tableEdit.save': 'Guardar cambios',
  'products.tableEdit.saving': 'Guardando…',
  'products.tableEdit.pendingCount': '{count} filas sin guardar',
  'products.tableEdit.cancelTitle': 'Cancelar edición en tabla',
  'products.tableEdit.discardConfirm': 'Descartar cambios',
  'products.tableEdit.cancelConfirm':
    'Se perderán los cambios sin guardar en las filas marcadas. Esta acción no se puede deshacer',
  'products.tableEdit.unsaved': 'Sin guardar',
  'products.tableEdit.nameSkuRequired': 'El nombre y el SKU no pueden quedar vacíos',
  'products.tableEdit.rowStatus': 'Estado de la fila',
  'products.tableEdit.rowFailed': 'No se guardó',
  'products.tableEdit.fieldLabel': '{field}: {name}',
  // The per-row reason follows this text in the banner itself, one entry per
  // SKU: the row's own status cell only has room for a badge.
  'products.tableEdit.saveFailed': '{count} filas no se pudieron guardar',

  // Terminal auto-provisioning panel (Terminales page) — 2026-07-31, see
  // docs/decisions/2026-07-31-provisioning-admin-panel.md.
  'provisioning.title': 'Dispositivos por vincular',
  'provisioning.subtitle': 'Terminales que se anuncian solas, en espera de aprobación',
  'provisioning.empty': 'Ningún dispositivo en espera de aprobación',
  'provisioning.name': 'Nombre',
  'provisioning.nameHint': 'El nombre debe ser único. Si ya existe, cámbielo aquí antes de aprobar',
  'provisioning.nameRequired': 'Capture un nombre',
  'provisioning.uuid': 'UUID',
  'provisioning.device': 'Dispositivo',
  'provisioning.firstSeen': 'Primera vez visto',
  'provisioning.lastSeen': 'Última vez visto',
  'provisioning.online': 'En línea',
  'provisioning.offline': 'Sin conexión',
  'provisioning.statusPending': 'Pendiente',
  'provisioning.statusApproved': 'Aprobado, en espera del dispositivo',
  'provisioning.statusClaimed': 'Vinculado',
  'provisioning.statusRejected': 'Rechazado',
  'provisioning.approve': 'Aprobar',
  'provisioning.approving': 'Aprobando…',
  'provisioning.approveSuccess': 'Aprobado. El dispositivo se configurará solo en los próximos 30 segundos',
  'provisioning.reject': 'Rechazar',
  'provisioning.rejectConfirm':
    'El dispositivo no podrá vincularse con esta solicitud. Puede volver a anunciarse para intentarlo de nuevo',
  'provisioning.fieldLabel': '{field}: {name}',
  'provisioning.copyUuidLabel': 'Copiar UUID de {name}',

  // Table sort & filter (2026-07-31 — shared mechanism across every admin
  // table; see docs/decisions/2026-07-31-admin-table-sort-filter.md)
  'table.sortBy': 'Ordenar por {column}',
  'table.filterBy': 'Filtrar por {column}',
  'table.filterContainsPlaceholder': 'Contiene…',
  'table.filterMinLabel': '{column}, mínimo',
  'table.filterMaxLabel': '{column}, máximo',
  'table.clearFilters': 'Limpiar filtros',
  'table.filtersActive': '{count} filtros activos',
  'table.noMatches': 'Ningún resultado con los filtros actuales',

  // Shift variance filter — a bare category label ("Sobrante"/"Faltante"),
  // distinct from `shifts.varianceOver`/`varianceShort` which always carry
  // an interpolated amount and are used only in the table cell itself.
  'shifts.varianceCategoryOver': 'Sobrante',
  'shifts.varianceCategoryShort': 'Faltante',

  // Discount reasons — this table's own activate label. Previously missing:
  // the page borrowed `categories.activate`, which happened to read
  // correctly but pointed at the wrong feature's catalog entry.
  'discountReasons.activate': 'Reactivar',
  'discountReasons.defaultPct': 'Porcentaje estándar',
  'discountReasons.defaultPctHint':
    'Opcional, de 0 a 100 con hasta dos decimales. Al elegir este motivo en la terminal, el descuento se llena solo con este porcentaje; el operador puede cambiarlo. Vacío = sin estándar.',
  'discountReasons.minSubtotal': 'Subtotal mínimo del ticket',
  'discountReasons.minSubtotalHint':
    'Opcional, en pesos. El motivo solo se ofrece en la terminal cuando el subtotal del ticket alcanza este monto. Vacío = siempre disponible.',

  // Ticket template editor
  'ticket.title': 'Diseño del ticket',
  'ticket.subtitle':
    'Nombre impreso, encabezado y pie. Los cambios llegan a las terminales en su siguiente sincronización, en menos de un minuto si están en línea.',
  'ticket.businessName': 'Nombre impreso del negocio',
  'ticket.businessNameHint': 'Se imprime centrado en letra grande: caben 16 caracteres por renglón.',
  'ticket.headerLabel': 'Encabezado',
  'ticket.headerHint':
    'Se imprime arriba del nombre del negocio. Cada renglón es una línea del ticket; el texto largo se ajusta solo a 32 columnas.',
  'ticket.footerLabel': 'Pie',
  'ticket.footerHint':
    'Se imprime al final del ticket. Cada renglón es una línea; un renglón vacío deja una línea en blanco.',
  'ticket.qrUrl': 'Enlace del código QR',
  'ticket.qrUrlHint':
    'Se imprime como código QR al final del ticket, después del pie. Déjelo vacío para no imprimir ningún código.',
  'ticket.preview': 'Vista previa',
  'ticket.previewHint': 'Generada con el mismo formato que usa la impresora, sobre una venta de ejemplo.',
  'ticket.save': 'Guardar cambios',
  'ticket.saved': 'Guardado. Las terminales lo reciben en su próxima sincronización.',
  'ticket.unsaved': 'Hay cambios sin guardar.',

  // Web-password reset for admin-role members
  'staff.setPassword': 'Restablecer contraseña',
  'staff.newPassword': 'Nueva contraseña',
  'staff.passwordConfirm': 'Confirme la contraseña',
  'staff.passwordMismatch': 'Las contraseñas no coinciden.',
  'staff.passwordNeverShown': 'La contraseña actual nunca se muestra; aquí solo se define una nueva.',
  'staff.passwordSet': 'Contraseña actualizada. Compártala con la persona por un medio seguro.',
  'app.newVersion': 'Hay una versión más reciente de esta página.',
  'app.reloadNow': 'Recargar',
};

// Deliberately not `as const`: the type of every value must widen to
// `string`, not the literal Spanish text, so packages/admin/src/i18n/en-US.ts
// (and any other locale) can assign a different string to the same key.
// @equinox/shared's own es-MX catalog uses `as const` and that is exactly
// why its en-US.ts fails to typecheck (see
// docs/decisions/2026-07-31-admin-i18n-catalog.md) — not repeating that bug
// here. Keys stay literal regardless; only values need to widen.
